import { Container, Graphics, Sprite, Texture, TilingSprite } from "pixi.js";
import { AssetKeys } from "./Assets";

interface ParallaxLayer {
  sprite: TilingSprite;
  speed: number;
  heightFactor: number;
  portraitHeightFactor: number;
}

const TREE_POOL_SIZE = 5;
const TREE_SPACING = 720;
const TREE_PARALLAX = 0.3;
const SKY_COLOR = 0x4a8770;

export class Background extends Container {
  private sky: Graphics;
  private layers: ParallaxLayer[] = [];
  private trees: Sprite[] = [];
  private viewW = 1920;
  private scrollX = 0;

  constructor(textures: Record<string, Texture>) {
    super();

    this.sky = new Graphics();
    this.addChild(this.sky);

    this.layers.push(this.mkLayer(textures[AssetKeys.BgFar], 0.18, 1.0, 1.0));

    for (let i = 0; i < TREE_POOL_SIZE; i++) {
      const t = new Sprite(textures[AssetKeys.TreeDecor]);
      t.anchor.set(0.5, 1);
      t.alpha = 0.85;
      this.trees.push(t);
      this.addChild(t);
    }
  }

  private mkLayer(
    tex: Texture,
    speed: number,
    heightFactor: number,
    portraitHeightFactor: number,
  ): ParallaxLayer {
    const sprite = new TilingSprite({ texture: tex, width: 100, height: 100 });
    this.addChild(sprite);
    return { sprite, speed, heightFactor, portraitHeightFactor };
  }

  resize(width: number, height: number) {
    this.viewW = width;

    this.sky.clear();
    this.sky.rect(0, 0, width, height);
    this.sky.fill({ color: SKY_COLOR });

    const portrait = height > width * 1.25;

    for (const layer of this.layers) {
      const tex = layer.sprite.texture;
      const layerH = height * (portrait ? layer.portraitHeightFactor : layer.heightFactor);
      const texScale = layerH / tex.height;
      layer.sprite.tileScale.set(texScale);
      layer.sprite.width = width;
      layer.sprite.height = layerH;
      layer.sprite.position.set(0, height - layerH);
    }

    const treeScale = (height * (portrait ? 0.55 : 0.7)) / this.trees[0].texture.height;
    const treeBaseY = height;
    for (const t of this.trees) {
      t.scale.set(treeScale);
      t.y = treeBaseY;
    }
  }

  update(dt: number, scrollSpeed: number) {
    this.scrollX += scrollSpeed * dt;

    for (const layer of this.layers) {
      layer.sprite.tilePosition.x = -this.scrollX * layer.speed;
    }

    const wrap = TREE_POOL_SIZE * TREE_SPACING;
    const offset = this.scrollX * TREE_PARALLAX;
    for (let i = 0; i < this.trees.length; i++) {
      let x = (i * TREE_SPACING - offset) % wrap;
      if (x < -200) x += wrap;
      this.trees[i].x = x;
    }
  }
}
